import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useId, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router';

import { ApiError } from '@shared/api/client';
import { resolveErrorCopy } from '@shared/errors/catalog';
import { Alert } from '@shared/ui/Alert';
import { cn } from '@shared/ui/cn';

import { PEOPLE_ROUTES } from '../_shared/constants';
import { peopleErrors } from '../_shared/errors';
import { useUpdateCreditProfile } from '../_shared/mutations';
import type { CreditProfileRecord } from '../_shared/types';

import { copy } from './copy';
import { personFieldsSchema, type PersonFieldInputs, type PersonFieldValues } from './forms.schema';
import { PersonDetailPortrait } from './PersonDetail.Portrait';

/**
 * The two public fields.
 *
 * **This is the one editable region on the page**, and it sits in a card while
 * the works table does not: raising the editable half a single elevation step
 * says which is which without a sentence of instructions, and the table needs no
 * card because its own scroll wrapper is already its panel.
 *
 * **Two columns from `--bp-md`: the fields in one, the portrait control in the
 * other.** A field caps at 30 rem and the card around it is about a thousand
 * pixels on a desktop, so a single column left half the card empty and the form
 * read as unfinished. The control **follows** the fields in the document so it is
 * read after them, and sits beside them so it is seen with them.
 *
 * **The portrait is not part of this form's submit**, and that is deliberate
 * rather than an omission. A file upload is a three-call chain against another
 * service that ends in its own PATCH; folding it into «احفظ» would make one
 * button mean two writes with two failure modes, and leave an operator who
 * only renamed the person waiting on a bucket.
 *
 * **The save confirmation is polite, not assertive.** A successful save is not
 * an error and must not interrupt a screen reader mid-sentence, so the region is
 * in the markup from load and revealed by the handler — revealed **before** its
 * text is rewritten, because a live region revealed and populated in one frame
 * is announced unreliably.
 */
export function PersonDetailForm({ person }: { person: CreditProfileRecord }) {
  const nameId = useId();
  const biographyId = useId();
  const biographyHintId = useId();

  const [saved, setSaved] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const updateProfile = useUpdateCreditProfile(person.slug);

  const form = useForm<PersonFieldInputs, unknown, PersonFieldValues>({
    resolver: zodResolver(personFieldsSchema),
    mode: 'onTouched',
    reValidateMode: 'onChange',

    defaultValues: { displayName: person.display_name, biography: person.biography ?? '' },
  });

  // The record arrives after the first paint on a cold load, and a rename
  // landing from another operator must not be overwritten by a stale default.
  const { reset } = form;
  useEffect(() => {
    reset({ displayName: person.display_name, biography: person.biography ?? '' });
  }, [person.id, person.display_name, person.biography, reset]);

  /** Move focus to the first field the resolver refused. */
  function focusFirstError(errors: Record<string, unknown>) {
    const first = Object.keys(errors)[0];
    if (!first) return;
    const field = document.querySelector<HTMLElement>(`[name="${first}"]`);
    field?.focus();
    field?.scrollIntoView({ block: 'center' });
  }

  function onSubmit(values: PersonFieldValues) {
    setSaved(false);
    setFailure(null);

    const biography = values.biography.length > 0 ? values.biography : null;

    updateProfile.mutate(
      {
        profileId: person.id,
        // `PATCH` is `exclude_unset` on the service side, and both fields are
        // genuinely on this form — an untouched one is still the value the
        // operator is choosing to keep.
        body: { display_name: values.displayName, biography },
      },
      {
        onSuccess: () => setSaved(true),
        onError: (error) => {
          const status = error instanceof ApiError ? error.status : 0;
          const code = error instanceof ApiError ? error.code : undefined;
          setFailure(resolveErrorCopy(code, status, peopleErrors).message);
        },
      },
    );
  }

  return (
    <div className="overflow-hidden rounded-md border border-border bg-surface">
      <div className="p-5 md:p-6">
        {/*
          **The caret goes to the problem, not to the button that reported it.**

          The refusal was already visible — `aria-invalid` was set and «أدخل اسم
          العرض.» rendered under the field — but focus stayed on «احفظ», so a
          keyboard or screen-reader user was told something was wrong and left
          standing on the control that told them, with nothing naming which field
          to go to. On a form long enough to scroll, a sighted operator could not
          see the message either.

          `shouldFocusError` is react-hook-form's default and was not enough here,
          so the move is made explicitly against the field the resolver named.
        */}
        <form noValidate onSubmit={form.handleSubmit(onSubmit, focusFirstError)}>
          <p className="mb-4 max-w-none text-xs text-ink-muted">
            {copy.fields.requiredNote}
            <span className="ms-[2px] text-danger" aria-hidden="true">
              *
            </span>
            {copy.fields.requiredNoteTail}
          </p>

          <div className="grid gap-x-6 md:grid-cols-2">
            <div className="min-w-0">
              <div className="mb-4">
                <label htmlFor={nameId} className="mb-2 block text-sm font-medium">
                  {copy.fields.name.label}
                  <span className="ms-[2px] text-danger" aria-hidden="true">
                    *
                  </span>
                </label>
                <input
                  {...form.register('displayName')}
                  id={nameId}
                  type="text"
                  required
                  aria-invalid={form.formState.errors.displayName ? true : undefined}
                  className="min-h-[2.75rem] w-full max-w-[30rem] rounded-sm border border-border-strong bg-surface-raised px-4 py-3 text-ink [font-size:var(--fs-body)] [line-height:normal] hover:border-ink-muted focus-visible:border-accent"
                />
                {form.formState.errors.displayName?.message ? (
                  <p className="mb-0 mt-2 text-xs text-danger">{form.formState.errors.displayName.message}</p>
                ) : null}
              </div>

              <div className="mb-4">
                <label htmlFor={biographyId} className="mb-2 block text-sm font-medium">
                  {copy.fields.biography.label}
                </label>
                <textarea
                  {...form.register('biography')}
                  id={biographyId}
                  rows={4}
                  aria-describedby={biographyHintId}
                  className="w-full max-w-[30rem] rounded-sm border border-border-strong bg-surface-raised px-4 py-3 text-ink [font-size:var(--fs-body)] [resize:vertical] hover:border-ink-muted focus-visible:border-accent"
                />
                <p id={biographyHintId} className="mb-0 mt-2 max-w-[30rem] text-xs text-ink-muted">
                  {copy.fields.biography.hint}
                </p>
              </div>
            </div>

            {/*
              Read after the fields, seen beside them. Placement is explicit
              rather than left to source order, so what a sighted reader sees
              and what a screen reader hears never diverge.
            */}
            <div className="min-w-0 self-start">
              <PersonDetailPortrait person={person} />
            </div>
          </div>

          {/*
            **Full-width stack below `--bp-sm`, one row from it.** The UI/UX
            pack fixes `[data-form-actions] .btn` at `inline-size: 100%` on the
            base tier and `auto` from 480 px, and states the reason there: two
            buttons sharing a line on a 320 px window drop below the 2.75 rem
            touch target. Neither tier centres them — the primary action sits at
            the reading-start edge on every surface in this console, and a row
            that centred here would be the one place an operator has to look
            somewhere new for «احفظ».
          */}
          <div className="mt-2 grid gap-3 sm:flex sm:flex-wrap">
            <button
              type="submit"
              disabled={updateProfile.isPending}
              className="hit-target w-full rounded-sm border border-accent bg-accent px-5 text-sm font-semibold text-accent-ink hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-45 sm:w-auto"
            >
              {copy.fields.save}
            </button>
            {/*
              A link, not a button: it navigates to the register and performs no
              action on this page. Secondary rather than ghost — a ghost with no
              fill and no border all but disappears on the dark canvas.
            */}
            <Link
              to={PEOPLE_ROUTES.register}
              className="hit-target inline-flex w-full items-center justify-center rounded-sm border border-border-strong bg-surface-raised px-5 text-sm font-semibold text-ink no-underline hover:bg-surface-overlay sm:w-auto"
            >
              {copy.fields.cancel}
            </Link>
          </div>

          <div role="status" className={cn('mt-5', !saved && 'hidden')}>
            <Alert kind="success" title={copy.fields.saved.title}>
              {copy.fields.saved.body}
            </Alert>
          </div>

          {/*
            No `role="alert"` on the wrapper: `<Alert kind="danger">` sets one
            itself, and two nested alert regions were announced twice by a screen
            reader and rendered as two identical banners — one `display:block`,
            one `display:flex` — under every failed save. This div is spacing only.
          */}
          {failure ? (
            <div className="mt-5">
              <Alert kind="danger">{failure}</Alert>
            </div>
          ) : null}
        </form>
      </div>
    </div>
  );
}
